import React, { useState } from 'react';
import { Building2, Users, Star, Plane, MapPin, Globe, Check, Plus, Utensils, Wine, Waves, Sparkles, Palmtree, UtensilsCrossed, Flag, Pencil } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { Grid } from '@/components/ui/Grid';
import { VStack, HStack } from '@/components/ui/Stack';
import { VenueMap } from '@/components/ui/VenueMap/VenueMap';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs/Tabs';
import { VenueImagesPanel } from '@/components/venues/VenueImagesPanel';
import type { VenueDetails } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface VenueStatProps {
  label: string;
  value: string | number | React.ReactNode;
  icon: React.ReactNode;
}

function VenueStat({ label, value, icon }: VenueStatProps) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg text-center">
      <div className="flex justify-center mb-1 text-primary-600">
        {icon}
      </div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

interface VenueDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  venue: VenueDetails | null;
  isShortlisted: boolean;
  onAddToShortlist?: () => void;
  isAddingToShortlist?: boolean;
  onVenueUpdate?: () => void; // Optional callback when venue is updated
  onEditVenue?: (venue: VenueDetails) => void; // Optional: open the venue edit form (unifies edit + images)
}

export function VenueDetailsModal({
  isOpen,
  onClose,
  venue,
  isShortlisted,
  onAddToShortlist,
  isAddingToShortlist = false,
  onVenueUpdate,
  onEditVenue,
}: VenueDetailsModalProps) {
  const [activeTab, setActiveTab] = useState('basic');

  if (!venue) return null;

  // Safely format TripAdvisor rating
  const formatRating = (rating: number | string | null | undefined): string => {
    if (rating === null || rating === undefined) return '';
    const numRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    return isNaN(numRating) ? '' : numRating.toFixed(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={venue.property_name}
      size="xl"
    >
      <VStack gap={6}>
        {/* Location */}
        <HStack gap={2} align="center" className="text-muted-foreground">
          <MapPin className="w-4 h-4" />
          <span>
            {venue.city}
            {venue.state && `, ${venue.state}`}
            {venue.country && `, ${venue.country}`}
          </span>
        </HStack>

        {/* Key Stats */}
        <Grid cols={2} gap={3} className="md:grid-cols-4">
          {venue.number_of_rooms && (
            <VenueStat
              label="Rooms"
              value={venue.number_of_rooms}
              icon={<Building2 className="w-5 h-5" />}
            />
          )}
          {venue.maximum_capacity && (
            <VenueStat
              label="Max Capacity"
              value={venue.maximum_capacity}
              icon={<Users className="w-5 h-5" />}
            />
          )}
          {venue.star_rating && (
            <VenueStat
              label="Star Rating"
              value={<StarRating rating={venue.star_rating || 0} size="sm" showEmptyStars={true} />}
              icon={<Star className="w-5 h-5" />}
            />
          )}
          {venue.nearest_airport_distance_km !== null && venue.nearest_airport_distance_km !== undefined && (
            <VenueStat
              label="Airport"
              value={`${Number(venue.nearest_airport_distance_km).toFixed(1)}km`}
              icon={<Plane className="w-5 h-5" />}
            />
          )}
        </Grid>

        {/* Tabbed Content */}
        <Tabs defaultValue="basic" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="amenities">Amenities</TabsTrigger>
            <TabsTrigger value="tripadvisor">TripAdvisor</TabsTrigger>
            <TabsTrigger value="images">Images</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic">
            <Card>
              <CardContent className="pt-6">
                <Grid cols={2} gap={3} className="text-sm">
                  <dt className="font-medium">Address:</dt>
                  <dd className="text-muted-foreground">{venue.address || 'N/A'}</dd>

                  {venue.website && (
                    <>
                      <dt className="font-medium">Website:</dt>
                      <dd>
                        <a
                          href={venue.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline flex items-center gap-1"
                        >
                          <Globe className="w-3 h-3" />
                          View Website →
                        </a>
                      </dd>
                    </>
                  )}

                  {venue.nearest_airport_name && (
                    <>
                      <dt className="font-medium">Nearest Airport:</dt>
                      <dd className="text-muted-foreground">
                        {venue.nearest_airport_name}
                        {venue.nearest_airport_iata && ` (${venue.nearest_airport_iata})`}
                      </dd>
                    </>
                  )}

                  {venue.meeting_spaces && (
                    <>
                      <dt className="font-medium">Meeting Spaces:</dt>
                      <dd className="text-muted-foreground">{venue.meeting_spaces}</dd>
                    </>
                  )}

                  <dt className="font-medium">Region:</dt>
                  <dd className="text-muted-foreground">{venue.region || 'N/A'}</dd>

                  <dt className="font-medium">Data Status:</dt>
                  <dd>
                    <Badge variant={venue.enrichment_status === 'enriched' ? 'success' : 'secondary'}>
                      {venue.enrichment_status}
                    </Badge>
                  </dd>
                </Grid>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Amenities Tab */}
          <TabsContent value="amenities">
            <Card>
              <CardContent className="pt-6">
                {(venue.number_of_restaurants !== null && venue.number_of_restaurants !== undefined) ||
                 (venue.number_of_bars !== null && venue.number_of_bars !== undefined) ||
                 (venue.number_of_pools !== null && venue.number_of_pools !== undefined) ||
                 venue.spa_facilities !== null || venue.beach_access || venue.all_inclusive_available !== null ||
                 venue.property_type || venue.golf_course ? (
                  <Grid cols={2} gap={3} className="text-sm">
                    {venue.number_of_restaurants !== null && venue.number_of_restaurants !== undefined && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Utensils className="w-4 h-4 text-primary-600" />
                          Restaurants:
                        </dt>
                        <dd className="text-muted-foreground">{venue.number_of_restaurants}</dd>
                      </>
                    )}
                    {venue.number_of_bars !== null && venue.number_of_bars !== undefined && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Wine className="w-4 h-4 text-primary-600" />
                          Bars/Lounges:
                        </dt>
                        <dd className="text-muted-foreground">{venue.number_of_bars}</dd>
                      </>
                    )}
                    {venue.number_of_pools !== null && venue.number_of_pools !== undefined && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Waves className="w-4 h-4 text-primary-600" />
                          Pools:
                        </dt>
                        <dd className="text-muted-foreground">{venue.number_of_pools}</dd>
                      </>
                    )}
                    {venue.spa_facilities !== null && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-primary-600" />
                          Spa Facilities:
                        </dt>
                        <dd className="text-muted-foreground">{venue.spa_facilities ? 'Yes' : 'No'}</dd>
                      </>
                    )}
                    {venue.beach_access && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Palmtree className="w-4 h-4 text-primary-600" />
                          Beach Access:
                        </dt>
                        <dd className="text-muted-foreground">
                          {venue.beach_access === 'beachfront' ? 'Beachfront' :
                           venue.beach_access === 'beach_access' ? 'Beach Access Available' :
                           venue.beach_access === 'none' ? 'None' : 'N/A'}
                        </dd>
                      </>
                    )}
                    {venue.all_inclusive_available !== null && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <UtensilsCrossed className="w-4 h-4 text-primary-600" />
                          All-Inclusive:
                        </dt>
                        <dd className="text-muted-foreground">
                          {venue.all_inclusive_available ? 'Available' : 'Not Available'}
                        </dd>
                      </>
                    )}
                    {venue.property_type && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Users className="w-4 h-4 text-primary-600" />
                          Property Type:
                        </dt>
                        <dd className="text-muted-foreground">
                          {venue.property_type === 'adults_only' ? 'Adults Only' :
                           venue.property_type === 'family_friendly' ? 'Family Friendly' : 'Mixed'}
                        </dd>
                      </>
                    )}
                    {venue.golf_course && (
                      <>
                        <dt className="font-medium flex items-center gap-1">
                          <Flag className="w-4 h-4 text-primary-600" />
                          Golf Course:
                        </dt>
                        <dd className="text-muted-foreground">
                          {venue.golf_course === 'onsite' ? 'On-site' :
                           venue.golf_course === 'nearby' ? 'Nearby (within 5km)' :
                           venue.golf_course === 'none' ? 'None' : 'N/A'}
                        </dd>
                      </>
                    )}
                  </Grid>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No amenity information available for this venue.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TripAdvisor Tab */}
          <TabsContent value="tripadvisor">
            <Card>
              <CardContent className="pt-6">
                {venue.tripadvisor_link || venue.tripadvisor_rating || (venue.tripadvisor_reviews_json && venue.tripadvisor_reviews_json.length > 0) ? (
                  <VStack gap={4}>
                    {/* TripAdvisor Info - Consistent Format with Basic Info */}
                    <Grid cols={2} gap={3} className="text-sm">
                      {/* Star Rating */}
                      {venue.tripadvisor_rating && formatRating(venue.tripadvisor_rating) && (
                        <>
                          <dt className="font-medium">Rating:</dt>
                          <dd className="text-muted-foreground flex items-center gap-2">
                            <StarRating rating={Number(formatRating(venue.tripadvisor_rating))} size="sm" showEmptyStars={false} />
                            <span className="font-medium">
                              {formatRating(venue.tripadvisor_rating)} out of 5
                            </span>
                          </dd>
                        </>
                      )}

                      {/* Number of Reviews */}
                      {venue.tripadvisor_review_count && (
                        <>
                          <dt className="font-medium">Number of Reviews:</dt>
                          <dd className="text-muted-foreground">
                            {venue.tripadvisor_review_count.toLocaleString()} reviews
                          </dd>
                        </>
                      )}

                      {/* TripAdvisor Link - Consistent with Website Link */}
                      {venue.tripadvisor_link && (
                        <>
                          <dt className="font-medium">TripAdvisor:</dt>
                          <dd>
                            <a
                              href={venue.tripadvisor_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline flex items-center gap-1"
                            >
                              <Globe className="w-3 h-3" />
                              View on TripAdvisor →
                            </a>
                          </dd>
                        </>
                      )}
                    </Grid>

                    {/* Reviews */}
                    {venue.tripadvisor_reviews_json && venue.tripadvisor_reviews_json.length > 0 && (
                      <div className="pt-4 border-t">
                        <h4 className="text-sm font-medium mb-3">Recent Reviews</h4>
                        <VStack gap={3}>
                          {venue.tripadvisor_reviews_json.map((review, idx) => (
                            <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900">{review.title}</h5>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    by {review.author} • {new Date(review.date).toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <StarRating rating={review.rating} size="sm" showEmptyStars={false} />
                                </div>
                              </div>
                              <p className="text-sm text-gray-700 leading-relaxed">{review.text}</p>
                            </div>
                          ))}
                        </VStack>
                      </div>
                    )}
                  </VStack>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No TripAdvisor information available for this venue.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Tab - Shared panel (same component as the Edit Venue modal) */}
          <TabsContent value="images">
            <Card>
              <CardContent className="pt-6">
                <VenueImagesPanel
                  venueId={venue.id}
                  placeId={venue.place_id}
                  onPlaceIdChanged={() => onVenueUpdate?.()}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* AI Match Reasoning */}
        {venue.match_reasoning && (
          <Card>
            <CardHeader>
              <CardTitle as="h3">Why This Venue?</CardTitle>
            </CardHeader>
            <CardContent>
              <HStack gap={3} align="start">
                {venue.match_score !== null && venue.match_score !== undefined && (
                  <Badge variant="success" className="text-lg font-semibold flex-shrink-0">
                    {Math.round(venue.match_score)}% match
                  </Badge>
                )}
                <p className="text-sm text-muted-foreground">{venue.match_reasoning}</p>
              </HStack>
            </CardContent>
          </Card>
        )}

        {/* Venue Map - Moved to Bottom */}
        {venue.lat && venue.lon ? (
          <VenueMap
            lat={venue.lat}
            lon={venue.lon}
            venueName={venue.property_name}
          />
        ) : (
          <div className="bg-gray-100 rounded-lg p-8 text-center">
            <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <span className="text-gray-500 text-sm">Location not yet geocoded</span>
          </div>
        )}

        {/* Actions */}
        <HStack gap={3} justify="end" className="pt-4 border-t">
          {onEditVenue && (
            <Button
              variant="secondary"
              leftIcon={<Pencil className="w-4 h-4" />}
              onClick={() => onEditVenue(venue)}
              className="mr-auto"
            >
              Edit Details
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!isShortlisted && onAddToShortlist ? (
            <Button
              variant="primary"
              leftIcon={<Plus />}
              onClick={onAddToShortlist}
              disabled={isAddingToShortlist}
              isLoading={isAddingToShortlist}
            >
              {isAddingToShortlist ? 'Adding...' : 'Add to Shortlist'}
            </Button>
          ) : (
            <Badge variant="success" className="px-4 py-2">
              <Check className="w-4 h-4 mr-1" />
              Already Shortlisted
            </Badge>
          )}
        </HStack>
      </VStack>
    </Modal>
  );
}
